import type { AlertItem } from "@/types/terminal";

/** Fields newer versions of /alerts add. Each is optional so an older API still works. */
export interface AlertExtras {
	z_score?: number | null;
	z_score_reason?: string | null;
}

export interface AlertsMeta {
	total?: number;
	oldest_alert_time?: number | null;
	/** "no_flags" only once the index is complete; "index_incomplete" before that. */
	empty_reason?: string | null;
	admission?: { max_price?: number; min_volume_usd?: number };
}

const Z_REASON: Record<string, string> = {
	no_token_stats: "no trade-size stats for that token",
	too_few_trades: "too few trades in that token to measure",
	zero_spread: "every trade in that token was the same size",
};
const Z_NOT_LOADED = "no trade-size stats loaded for that market";

/** The list's admission rule in words. Uses what the server says it applied, when it says (pure). */
export function ruleText(admission?: AlertsMeta["admission"]): string {
	const usd = (admission?.min_volume_usd ?? 4_000) / 1_000;
	const cents = Math.round((admission?.max_price ?? 0.95) * 100);
	return `Rule, per wallet: at most 3 tokens and 60 trades, sold no more than 75% of what it bought, average entry 30¢ or less, and $4K+ bought ($1K+ if that is 5%+ of a token's volume). Listed: positions of $${usd}K+ entered at up to ${cents}¢. A flag is a statistical pattern, not evidence of inside information.`;
}

/** Keeps only `wallet`'s rows: an API that ignores the filter must not pin others' flags on it (pure). */
export function onlyWallet<T extends { wallet: string }>(rows: T[], wallet: string): T[] {
	const w = wallet.toLowerCase();
	return rows.filter((r) => r.wallet.toLowerCase() === w);
}

export interface FlaggedRow {
	id: string;
	time: number;
	conditionId: string | null;
	question: string;
	outcome: string;
	wallet: string;
	volume: number;
	entry: number;
	now: number;
	/** Size against the token's mean trade, in standard deviations. Null when stats are not loaded. */
	z: number | null;
	/** Why z is null, in words. */
	zWhy: string | null;
	/** How the market resolved relative to the flagged side. A flag is not a prediction. */
	status: "for" | "against" | "open";
	markets: number;
	category: string | null;
}

export interface TokenStats {
	mean: number | null;
	stdDev: number | null;
}

/** Same formula as the server-side `min_zscore` filter. Null when there is no spread to measure. */
export function positionZ(volume: number, stats: TokenStats | undefined): number | null {
	if (!stats || stats.stdDev === null || stats.stdDev === 0) return null;
	return (volume - (stats.mean ?? 0)) / stats.stdDev;
}

export function toFlaggedRow(a: AlertItem & AlertExtras, stats?: Map<string, TokenStats>): FlaggedRow {
	const volume = Number(a.volume || 0);
	// The server's z covers every token; the local one only those in the loaded stats.
	const z = a.z_score ?? (a.tokenId ? positionZ(volume, stats?.get(String(a.tokenId))) : null);
	return {
		id: `${a.user}:${a.tokenId ?? a.conditionId ?? a.question}:${a.alert_time}`,
		time: Number(a.alert_time || 0),
		conditionId: a.conditionId,
		question: a.question ?? a.conditionId ?? "Unknown market",
		outcome: a.outcome ?? "–",
		wallet: a.walletAddress ?? a.user,
		volume,
		entry: Number(a.price || 0),
		now: Number(a.market_price || 0),
		z,
		zWhy: z === null ? ((a.z_score_reason && Z_REASON[a.z_score_reason]) ?? Z_NOT_LOADED) : null,
		status: a.winner === true ? "for" : a.winner === false ? "against" : "open",
		markets: Number(a.market_count || 0),
		category: a.category,
	};
}

export interface FlowBlock {
	wallet: string;
	usd: number;
	side: "first" | "other";
	outcome: string;
}

export interface FlowMarket {
	conditionId: string;
	question: string;
	category: string | null;
	total: number;
	firstUsd: number;
	otherUsd: number;
	wallets: number;
	blocks: FlowBlock[];
}

const isFirstOutcome = (o: string) => /^(yes|over|up|1)$/i.test(o.trim());

/**
 * Group flagged positions by market, one block per wallet and side, largest first.
 * The input is whatever pages are loaded, so totals describe that sample only.
 */
export function buildFlow(rows: FlaggedRow[]): FlowMarket[] {
	const byMarket = new Map<string, FlowMarket & { byWallet: Map<string, FlowBlock> }>();
	for (const r of rows) {
		if (!r.conditionId) continue;
		let m = byMarket.get(r.conditionId);
		if (!m) {
			m = {
				conditionId: r.conditionId,
				question: r.question,
				category: r.category,
				total: 0,
				firstUsd: 0,
				otherUsd: 0,
				wallets: 0,
				blocks: [],
				byWallet: new Map(),
			};
			byMarket.set(r.conditionId, m);
		}
		const side = isFirstOutcome(r.outcome) ? "first" : "other";
		const key = `${r.wallet}:${side}`;
		const block = m.byWallet.get(key);
		if (block) block.usd += r.volume;
		else m.byWallet.set(key, { wallet: r.wallet, usd: r.volume, side, outcome: r.outcome });
		m.total += r.volume;
		if (side === "first") m.firstUsd += r.volume;
		else m.otherUsd += r.volume;
	}
	return [...byMarket.values()]
		.map(({ byWallet, ...m }) => {
			const blocks = [...byWallet.values()].sort((a, b) => b.usd - a.usd);
			return { ...m, blocks, wallets: new Set(blocks.map((b) => b.wallet)).size };
		})
		.sort((a, b) => b.total - a.total);
}
